import { inject, Service } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import * as fromActivateGroupDialog from '@features/account/settings/acc-folder/acc-folder-qr-urls/activate-group-dialog/activate-group-dialog.component';
import * as fromFoodbackCustomerContactDialog from '@features/venue/foodbacks/all-foodbacks/components/foodback-update-contact/foodback-update-contact.component';
import {
  FoodbackStatistic,
  IFoodbackContactStatus,
} from '@features/venue/foodbacks/all-foodbacks/foodback-statistic';
import {
  AskFrequencyOverrideDialogComponent,
  askFrequencyOverrideDialogConfig,
} from '@shared/components/ask-frequency/ask-frequency-override-dialog/ask-frequency-override-dialog.component';
import {
  AskFrequencyOverrideDialogData,
  AskFrequencyOverrideDialogResult,
} from '@shared/components/ask-frequency/ask-frequency-override-dialog/ask-frequency-override-dialog.interface';
import * as fromConfirmDialog from '@shared/components/dialog/dialogs/confirm-dialog/confirm-dialog.component';
import * as fromAddQuestionDialog from '@shared/components/foodback-questions/add-question-dialog/add-question-dialog.component';
import {
  AddQuestionDialogInput,
  AddQuestionDialogResult,
} from '@shared/components/foodback-questions/add-question-dialog/add-question-dialog.interface';
import * as fromConfigureCategories from '@shared/components/foodback-questions/configure-categories-dialog/configure-categories-dialog.component';
import { ConfigureCategoriesDialogInput } from '@shared/components/foodback-questions/configure-categories-dialog/configure-categories-dialog.interface';
import * as fromAddVenuesDialog from '@shared/components/groups/add-venues-dialog/add-venues-dialog.component';
import * as fromNewGroupCategoryDialog from '@shared/components/groups/new-category-dialog/new-category-dialog.component';
import * as fromNewGroupDialog from '@shared/components/groups/new-group-dialog/new-group-dialog.component';
import * as fromProfileAvatarDialog from '@shared/components/profile-avatar/components/profile-avartar-cropper-dialog/profile-avatar-cropper-dialog.component';
import {
  ResultDialogComponent,
  resultDialogConfig,
} from '@shared/components/result-dialog/result-dialog.component';
import { ResultDialogChoice, ResultReport } from '@shared/components/result-dialog/result-report';
import {
  TranslateDialogComponent,
  TranslateDialogData,
  TranslateDialogResult,
  translateDialogConfig,
} from '@shared/components/translate-dialog/translate-dialog.component';
import { ConfirmDialogInterface } from '@shared/interfaces/confirm-dialog.interface';
import { IGroup } from '@shared/interfaces/groups/group.interface';
import { ImageCropperInterface } from '@shared/interfaces/image-cropper.interface';

@Service()
export class DialogService {
  private matDialog = inject(MatDialog);

  newGroupCategory(data: IGroup | null) {
    return this.matDialog.open(fromNewGroupCategoryDialog.NewCategoryDialogComponent, {
      data,
      ...fromNewGroupCategoryDialog.newCategoryDialogConfig,
    });
  }

  newGroup(data: fromNewGroupDialog.NewGroupDialogData) {
    return this.matDialog.open(fromNewGroupDialog.NewGroupDialogComponent, {
      data,
      ...fromNewGroupDialog.newGroupDialogConfig,
    });
  }

  addVenuesToGroup(data: fromAddVenuesDialog.AddVenuesDialogData) {
    return this.matDialog.open(fromAddVenuesDialog.AddVenuesDialogComponent, {
      data,
      ...fromAddVenuesDialog.addVenuesDialogConfig,
    });
  }

  questionDialog(
    data: AddQuestionDialogInput
  ): MatDialogRef<
    fromAddQuestionDialog.AddQuestionDialogComponent,
    AddQuestionDialogResult | undefined
  > {
    return this.matDialog.open(fromAddQuestionDialog.AddQuestionDialogComponent, {
      data,
      ...fromAddQuestionDialog.addQuestionDialogConfig,
    });
  }

  askFrequencyOverrideDialog(
    data: AskFrequencyOverrideDialogData
  ): MatDialogRef<
    AskFrequencyOverrideDialogComponent,
    AskFrequencyOverrideDialogResult | undefined
  > {
    return this.matDialog.open(AskFrequencyOverrideDialogComponent, {
      data,
      ...askFrequencyOverrideDialogConfig,
    });
  }

  configureCategoriesDialog(data: ConfigureCategoriesDialogInput) {
    return this.matDialog.open(fromConfigureCategories.ConfigureCategoriesDialogComponent, {
      data,
      ...fromConfigureCategories.configureCategoriesDialogConfig,
    });
  }

  profileAvatarCropperDialog(data: ImageCropperInterface) {
    return this.matDialog.open(fromProfileAvatarDialog.ProfileAvatarCropperDialogComponent, {
      data,
      ...fromProfileAvatarDialog.profileAvatarCropperDialogConfig,
    });
  }

  confirmDialog(data: ConfirmDialogInterface) {
    return this.matDialog.open(fromConfirmDialog.ConfirmDialogComponent, {
      data,
      ...fromConfirmDialog.confirmDialogConfig,
    });
  }

  resultDialog(report: ResultReport): MatDialogRef<ResultDialogComponent, ResultDialogChoice> {
    return this.matDialog.open(ResultDialogComponent, {
      data: report,
      ...resultDialogConfig(report),
    });
  }

  activeGroupDialog() {
    return this.matDialog.open(
      fromActivateGroupDialog.ActivateGroupDialogComponent,
      fromActivateGroupDialog.activateGroupDialogConfig
    );
  }

  translateDialog(
    data: TranslateDialogData
  ): MatDialogRef<TranslateDialogComponent, TranslateDialogResult | undefined> {
    return this.matDialog.open(TranslateDialogComponent, {
      data,
      ...translateDialogConfig,
    });
  }

  updateContactCustomerDialog(
    data: FoodbackStatistic
  ): MatDialogRef<
    fromFoodbackCustomerContactDialog.FoodbackUpdateContactComponent,
    IFoodbackContactStatus
  > {
    return this.matDialog.open<
      fromFoodbackCustomerContactDialog.FoodbackUpdateContactComponent,
      FoodbackStatistic,
      IFoodbackContactStatus
    >(fromFoodbackCustomerContactDialog.FoodbackUpdateContactComponent, {
      data,
      ...fromFoodbackCustomerContactDialog.customContactDialogConfig,
    });
  }
}
